/**
 * Section header component for PA form sections.
 * Renders a titled header with contextual action icons depending on the section type.
 * Attachments sections show a refresh icon that enqueues a clinical docs RPA task.
 * Drug sections show eye icons for viewing drug details, prescription, and diagnosis codes.
 * @module renderSectionHeader
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { openModal } from "risa-oasis-ui_v2";
import { clinicalDocsRpaTrigger } from "../../../shared/api/postCall/clinicalDocsRpaTrigger";
import EyeIcon from "../../../shared/svg/eye";
import RefreshIcon from "../../../shared/svg/refresh-icon";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import DiagnosisCodeModal from "../modals/diagnosisCodeModal/diagnosisCodeModal";
import DrugDetailsModal from "../modals/drugDetailModal/drugDetailsModal";
import PrescriptionModal from "../modals/prescriptionModal/prescriptionModal";
import { ToastMessages } from "../toasts/toastMessages";

/**
 * Props for the RenderSectionHeader component.
 */
interface RenderSectionHeaderProps {
  /** Display title of the section. Controls which action icons are shown. */
  sectionTitle: string;
  /** Unique key for the section, used as the DOM element id and for icon logic. */
  sectionKey: string;
  /** Order identifier forwarded to modals (e.g. PrescriptionModal). */
  id: string;
}

/**
 * Renders a PA form section header with context-sensitive action icons.
 * Uses the `id` route param as the order ID when enqueueing the clinical docs RPA task,
 * ensuring the correct order is targeted regardless of how the component is instantiated.
 *
 * @param props - Section title, key, and order identifier
 */
const RenderSectionHeader = (props: RenderSectionHeaderProps) => {
  const { id: orderId } = useParams();
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [isDiagnosisCodesModalOpen, setIsDiagnosisCodesModalOpen] =
    useState(false);
  const [isDrugDetailsModalOpen, setIsDrugDetailsModalOpen] = useState(false);
  useEffect(() => {
    if (isPrescriptionModalOpen) {
      openModal("prescription-modal");
    }
    if (isDiagnosisCodesModalOpen) {
      openModal("diagnosis-codes-modal");
    }
    if (isDrugDetailsModalOpen) {
      openModal("drug-details-modal");
    }
  }, [
    isPrescriptionModalOpen,
    isDiagnosisCodesModalOpen,
    isDrugDetailsModalOpen,
  ]);

  return (
    <div
      className="single-section__header mb-2 flex items-center justify-between bg-primaryGray-16 px-3 py-2 text-body font-semibold text-primaryGray-1"
      id={props.sectionKey}
    >
      {props.sectionTitle}
      {props.sectionTitle.includes("Attachments") && (
        <div
          className="flex w-full justify-end"
          onClick={() => {
            clinicalDocsRpaTrigger({
              order_id: orderId ?? "",
              org_id: getOrgIdForFetchExternalWorklist() ?? "",
            })
              .then(() =>
                toast.success(
                  ToastMessages.CLINICAL_ATTACHMENT_REFETCHED_SUCCESS,
                ),
              )
              .catch(() =>
                toast.error(
                  ToastMessages.CLINICAL_ATTACHMENT_REFETCHED_FAILURE,
                ),
              );
          }}
        >
          <RefreshIcon />
        </div>
      )}
      {props.sectionKey.includes("drug") && (
        <div className="single-section__header-with-icon h flex items-center gap-4 hover:cursor-pointer">
          <div
            className="flex items-center gap-1"
            onClick={() => setIsDrugDetailsModalOpen(true)}
          >
            <div className="single-section__header-icon">
              <EyeIcon />
            </div>
            <div className="single-section__header-text text-small font-semibold text-tertiaryBlue-4">
              View Drug Details
            </div>
          </div>
          <div
            className="flex items-center gap-1"
            onClick={() => setIsPrescriptionModalOpen(true)}
          >
            <div className="single-section__header-icon">
              <EyeIcon />
            </div>
            <div className="single-section__header-text text-small font-semibold text-tertiaryBlue-4">
              View Prescription
            </div>
          </div>
          <div
            className="flex items-center gap-1"
            onClick={() => setIsDiagnosisCodesModalOpen(true)}
          >
            <div className="single-section__header-icon">
              <EyeIcon />
            </div>
            <div className="single-section__header-text text-small font-semibold text-tertiaryBlue-4">
              View Diagnosis Codes
            </div>
          </div>
        </div>
      )}
      {isPrescriptionModalOpen && (
        <PrescriptionModal
          onClose={() => setIsPrescriptionModalOpen(false)}
          id={props.id}
        />
      )}
      {isDiagnosisCodesModalOpen && (
        <DiagnosisCodeModal
          onClose={() => setIsDiagnosisCodesModalOpen(false)}
        />
      )}
      {isDrugDetailsModalOpen && (
        <DrugDetailsModal onClose={() => setIsDrugDetailsModalOpen(false)} />
      )}
    </div>
  );
};

export default RenderSectionHeader;
